import React, { useState, useEffect } from 'react';
import { shopkeeperService } from '../services/api';

interface Shopkeeper {
  _id: string;
  name: string;
  contactNo: string;
  officeNo?: string;
  email?: string;
  photo?: string;
  mobile: string;
  vehicleNumber?: string;
  phoneNumbers?: string[];
  vehicleNumbers?: string[];
}

interface RentalOwner {
  _id: string;
  name: string;
  phoneNumber: string;
  phoneNumbers?: string[];
  email?: string;
  photo?: string;
  vehicleNumber: string;
  vehicleNumbers?: string[];
  vehicleType: string;
}

interface Employee {
  _id: string;
  name: string;
  phoneNumber: string;
  phoneNumbers?: string[];
  email?: string;
  photo?: string;
  vehicleNumber: string;
  vehicleNumbers?: string[];
  vehicleType: string;
}

interface Shop {
  _id: string;
  shopName: string;
  officeNo?: string;
  squareFeet: number;
  maintenanceFee: number;
  shopkeeperId: Shopkeeper;
  nextDueDate?: string;
  userType?: 'shop_owner' | 'rental';
  rentalOwner?: RentalOwner | null;
  employees?: Employee[];
}

const Shopkeepers: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [expandedShop, setExpandedShop] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    shopName: '',
    squareFeet: '',
    password: '',
    vehicleNumber: '',
    vehicleNumbers: [] as string[],
    phoneNumbers: [] as string[],
    email: '',
    officeNo: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    contactNo: '',
    officeNo: '',
    email: '',
    password: '',
    shopName: '',
    squareFeet: '',
    photo: null as File | null,
    validTill: '',
    vehicleNumber: '',
    vehicleNumbers: [] as string[],
    phoneNumbers: [] as string[],
  });
  const [showAddOfficeModal, setShowAddOfficeModal] = useState(false);
  const [addOfficeForm, setAddOfficeForm] = useState({
    name: '',
    contactNo: '',
    email: '',
    shopName: '',
    squareFeet: '',
    officeNo: '',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalShops: 0,
    limit: 10
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  useEffect(() => {
    loadShopkeepers(pagination.currentPage, activeSearch);
  }, [pagination.currentPage, activeSearch]);

  const loadShopkeepers = async (page = 1, search = activeSearch) => {
    setLoading(true);
    try {
      const response = await shopkeeperService.getAll(page, pagination.limit, search);
      const { shops, pagination: meta } = response.data.data;
      setShops(shops);
      if (meta) {
        setPagination(prev => ({
          ...prev,
          currentPage: meta.currentPage,
          totalPages: meta.totalPages,
          totalShops: meta.totalShops
        }));
      }
    } catch (error) {
      console.error('Error loading shopkeepers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchTerm);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const isValidityExpired = (nextDueDate?: string): boolean => {
    if (!nextDueDate) return false;
    const dueDate = new Date(nextDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const getDaysUntilExpiry = (nextDueDate?: string): number | null => {
    if (!nextDueDate) return null;
    const dueDate = new Date(nextDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, photo: e.target.files[0] });
    }
  };

  const openEditModal = (shop: Shop) => {
    setEditingShop(shop);
    setEditForm({
      shopName: shop.shopName,
      squareFeet: shop.squareFeet.toString(),
      password: '',
      vehicleNumber: shop.shopkeeperId?.vehicleNumber || '',
      vehicleNumbers: shop.shopkeeperId?.vehicleNumbers || [],
      phoneNumbers: shop.shopkeeperId?.phoneNumbers || [],
      email: shop.shopkeeperId?.email || '',
      officeNo: shop.officeNo || '',
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingShop) return;
    try {
      const payload: any = {
        shopName: editForm.shopName,
        squareFeet: Number(editForm.squareFeet),
        officeNo: editForm.officeNo,
      };

      if (editForm.password.trim()) {
        payload.password = editForm.password.trim();
      }

      if (editForm.vehicleNumber.trim()) {
        payload.vehicleNumber = editForm.vehicleNumber.trim();
      }

      if (editForm.vehicleNumbers.length > 0) {
        payload.vehicleNumbers = editForm.vehicleNumbers.filter(v => v.trim() !== '');
      }

      if (editForm.phoneNumbers.length > 0) {
        payload.phoneNumbers = editForm.phoneNumbers.filter(p => p.trim() !== '');
      }

      if (editForm.email.trim()) {
        payload.email = editForm.email.trim();
      }

      await shopkeeperService.update(editingShop._id, payload);
      setShowEditModal(false);
      setEditingShop(null);
      await loadShopkeepers();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error updating shop');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('contactNo', formData.contactNo);
      data.append('officeNo', formData.officeNo || '');
      data.append('email', formData.email || '');
      data.append('password', formData.password || '123456');
      data.append('shopName', formData.shopName);
      data.append('squareFeet', formData.squareFeet);
      if (formData.vehicleNumber) {
        data.append('vehicleNumber', formData.vehicleNumber);
      }
      if (formData.vehicleNumbers.length > 0) {
        formData.vehicleNumbers.forEach((vn, index) => {
          if (vn.trim()) {
            data.append(`vehicleNumbers[${index}]`, vn.trim());
          }
        });
      }
      if (formData.phoneNumbers.length > 0) {
        formData.phoneNumbers.forEach((pn, index) => {
          if (pn.trim()) {
            data.append(`phoneNumbers[${index}]`, pn.trim());
          }
        });
      }
      if (formData.validTill) {
        data.append('validTill', formData.validTill);
      }
      if (formData.photo) {
        data.append('photo', formData.photo);
      }

      await shopkeeperService.create(data);
      setShowAddModal(false);
      setFormData({
        name: '',
        contactNo: '',
        officeNo: '',
        email: '',
        password: '',
        shopName: '',
        squareFeet: '',
        photo: null,
        validTill: '',
        vehicleNumber: '',
        vehicleNumbers: [],
        phoneNumbers: [],
      });
      loadShopkeepers(pagination.currentPage);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error creating shopkeeper');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Shopkeepers Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
        >
          <span>➕</span> Add Shopkeeper  
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input
              type="text"
              placeholder="Search by shop name, office no, name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all text-sm"
            />
          </div>
          <button
            type="submit"
            className="bg-primary-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-600 transition-colors shadow-sm"
          >
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setActiveSearch('');
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
              className="text-gray-500 hover:text-gray-700 font-medium px-2 text-sm"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Shop / Office</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Shopkeeper Info</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Vehicle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Size / Fee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Valid Till</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shops.map((shop) => {
                const expired = isValidityExpired(shop.nextDueDate);
                const daysLeft = getDaysUntilExpiry(shop.nextDueDate);
                const isExpanded = expandedShop === shop._id;
                const isRental = shop.userType === 'rental';

                return (
                  <React.Fragment key={shop._id}>
                    <tr className={`hover:bg-gray-50 transition-colors ${expired ? 'bg-red-50' : daysLeft !== null && daysLeft <= 7 ? 'bg-yellow-50' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900">{shop.shopName}</span>
                          <span className="text-xs text-gray-500">Office: {shop.officeNo || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {shop.shopkeeperId?.photo ? (
                            <img src={shop.shopkeeperId.photo} alt={shop.shopkeeperId.name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-semibold">
                              {shop.shopkeeperId?.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{shop.shopkeeperId?.name || '-'}</span>
                            <span className="text-xs text-gray-500">{shop.shopkeeperId?.mobile || shop.shopkeeperId?.contactNo || '-'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold w-fit ${isRental ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                            {isRental ? 'RENTAL' : 'OWNER'}
                          </span>
                          {isRental && (
                            <button
                              type="button"
                              onClick={() => setExpandedShop(isExpanded ? null : shop._id)}
                              className="text-[10px] text-primary-600 hover:text-primary-800 font-bold flex items-center gap-0.5"
                            >
                              {isExpanded ? '▼ HIDE' : '▶ DETAILS'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                        <div className="flex flex-col gap-0.5">
                          {shop.shopkeeperId?.vehicleNumber && <span>{shop.shopkeeperId.vehicleNumber}</span>}
                          {shop.shopkeeperId?.vehicleNumbers && shop.shopkeeperId.vehicleNumbers.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {shop.shopkeeperId.vehicleNumbers.map((vn, idx) => (
                                <span key={idx} className="text-[10px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded border border-blue-100">{vn}</span>
                              ))}
                            </div>
                          )}
                          {!shop.shopkeeperId?.vehicleNumber && (!shop.shopkeeperId?.vehicleNumbers || shop.shopkeeperId.vehicleNumbers.length === 0) && '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-gray-700">{shop.squareFeet} sq ft</span>
                          <span className="text-xs font-bold text-green-700">₹{Number(shop.maintenanceFee).toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {shop.nextDueDate ? (
                          <div className="flex flex-col">
                            <span className={`text-xs font-medium ${expired ? 'text-red-600' : daysLeft !== null && daysLeft <= 7 ? 'text-yellow-600' : 'text-gray-900'}`}>
                              {new Date(shop.nextDueDate).toLocaleDateString()}
                            </span>
                            {expired ? (
                              <span className="text-[10px] text-red-600 font-bold">EXPIRED</span>
                            ) : daysLeft !== null && daysLeft <= 7 ? (
                              <span className="text-[10px] text-yellow-600 font-bold">{daysLeft}d left</span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Not Set</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex gap-3">
                          <button type="button" onClick={() => openEditModal(shop)} className="text-primary-600 hover:text-primary-800 transition-colors" title="Edit">
                            <span className="text-lg">✏️</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAddOfficeForm({
                                name: shop.shopkeeperId?.name || '',
                                contactNo: shop.shopkeeperId?.contactNo || '',
                                email: shop.shopkeeperId?.email || '',
                                shopName: '',
                                squareFeet: '',
                                officeNo: '',
                              });
                              setShowAddOfficeModal(true);
                            }}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Add Office"
                          >
                            <span className="text-lg">🏢</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && isRental && (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-gray-50 border-y border-gray-200 shadow-inner">
                          <div className="space-y-6">
                            <div>
                              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="text-lg">🛻</span> Rental Owner Details
                              </h4>
                              {shop.rentalOwner ? (
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Photo</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Name</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Contact</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Vehicle Info</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      <tr>
                                        <td className="px-4 py-2">
                                          {shop.rentalOwner.photo ? (
                                            <img src={shop.rentalOwner.photo} alt={shop.rentalOwner.name} className="w-10 h-10 rounded-lg object-cover border border-gray-100" />
                                          ) : (
                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs font-bold">NO PIC</div>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-sm font-semibold text-gray-900">{shop.rentalOwner.name}</td>
                                        <td className="px-4 py-2">
                                          <div className="flex flex-col">
                                            <span className="text-sm text-gray-900">{shop.rentalOwner.phoneNumber}</span>
                                            <span className="text-xs text-gray-500">{shop.rentalOwner.email || 'No Email'}</span>
                                            {shop.rentalOwner.phoneNumbers && shop.rentalOwner.phoneNumbers.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mt-1">
                                                {shop.rentalOwner.phoneNumbers.map((pn, idx) => (
                                                  <span key={idx} className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-100">{pn}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-2">
                                          <div className="flex flex-col">
                                            <span className="text-sm text-gray-900 font-medium">{shop.rentalOwner.vehicleNumber}</span>
                                            <span className="text-xs text-gray-500">{shop.rentalOwner.vehicleType}</span>
                                            {shop.rentalOwner.vehicleNumbers && shop.rentalOwner.vehicleNumbers.length > 0 && (
                                              <div className="flex flex-wrap gap-1 mt-1">
                                                {shop.rentalOwner.vehicleNumbers.map((vn, idx) => (
                                                  <span key={idx} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{vn}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 italic">No rental owner details available</p>
                              )}
                            </div>

                            <div>
                              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="text-lg">👥</span> Employees ({shop.employees?.length || 0})
                              </h4>
                              {shop.employees && shop.employees.length > 0 ? (
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Photo</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Name</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Contact</th>
                                        <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Vehicle Info</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {shop.employees.map((employee) => (
                                        <tr key={employee._id} className="hover:bg-gray-50">
                                          <td className="px-4 py-2">
                                            {employee.photo ? (
                                              <img src={employee.photo} alt={employee.name} className="w-10 h-10 rounded-lg object-cover border border-gray-100" />
                                            ) : (
                                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs font-bold">NO PIC</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{employee.name}</td>
                                          <td className="px-4 py-2">
                                            <div className="flex flex-col">
                                              <span className="text-sm text-gray-900">{employee.phoneNumber}</span>
                                              <span className="text-xs text-gray-500">{employee.email || 'No Email'}</span>
                                              {employee.phoneNumbers && employee.phoneNumbers.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                  {employee.phoneNumbers.map((pn, idx) => (
                                                    <span key={idx} className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-100">{pn}</span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-4 py-2">
                                            <div className="flex flex-col">
                                              <span className="text-sm text-gray-900 font-medium">{employee.vehicleNumber}</span>
                                              <span className="text-xs text-gray-500">{employee.vehicleType}</span>
                                              {employee.vehicleNumbers && employee.vehicleNumbers.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                  {employee.vehicleNumbers.map((vn, idx) => (
                                                    <span key={idx} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{vn}</span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 italic">No employees added</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
              disabled={pagination.currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.limit + 1}</span> to <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.totalShops)}</span> of{' '}
                <span className="font-medium">{pagination.totalShops}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: 1 }))}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">First</span>
                  «
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  ‹
                </button>

                {[...Array(pagination.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === pagination.currentPage - 2 ||
                    pageNum === pagination.currentPage + 2
                  ) {
                    return <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(pagination.totalPages, prev.currentPage + 1) }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  ›
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: pagination.totalPages }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">Last</span>
                  »
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Add New Shopkeeper</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact No *</label>
                  <input
                    type="text"
                    name="contactNo"
                    value={formData.contactNo}
                    onChange={handleInputChange}
                    maxLength={10}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Office No</label>
                  <input
                    type="text"
                    name="officeNo"
                    value={formData.officeNo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Default: 123456"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleInputChange}
                    placeholder="Primary vehicle number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Vehicle Numbers</label>
                  {formData.vehicleNumbers.map((vn, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={vn}
                        onChange={(e) => {
                          const newNumbers = [...formData.vehicleNumbers];
                          newNumbers[index] = e.target.value;
                          setFormData({ ...formData, vehicleNumbers: newNumbers });
                        }}
                        placeholder="Additional vehicle number"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newNumbers = formData.vehicleNumbers.filter((_, i) => i !== index);
                          setFormData({ ...formData, vehicleNumbers: newNumbers });
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicleNumbers: [...formData.vehicleNumbers, ''] })}
                    className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    + Add Vehicle Number
                  </button>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Phone Numbers</label>
                  {formData.phoneNumbers.map((pn, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={pn}
                        onChange={(e) => {
                          const newNumbers = [...formData.phoneNumbers];
                          newNumbers[index] = e.target.value;
                          setFormData({ ...formData, phoneNumbers: newNumbers });
                        }}
                        placeholder="Additional phone number"
                        maxLength={10}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newNumbers = formData.phoneNumbers.filter((_, i) => i !== index);
                          setFormData({ ...formData, phoneNumbers: newNumbers });
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, phoneNumbers: [...formData.phoneNumbers, ''] })}
                    className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    + Add Phone Number
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Previous Validity (optional)</label>
                  <input
                    type="date"
                    name="validTill"
                    value={formData.validTill}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <small className="text-gray-500 text-xs mt-1 block">
                    If set, subscription is considered valid till this date for the first cycle.
                  </small>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name *</label>
                  <input
                    type="text"
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Feet *</label>
                  <input
                    type="number"
                    name="squareFeet"
                    value={formData.squareFeet}
                    onChange={handleInputChange}
                    min="0.1"
                    step="any"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingShop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Edit Shop / Shopkeeper</h3>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name *</label>
                  <input
                    type="text"
                    name="shopName"
                    value={editForm.shopName}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Feet *</label>
                  <input
                    type="number"
                    name="squareFeet"
                    value={editForm.squareFeet}
                    onChange={handleEditChange}
                    min="0.1"
                    step="any"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Office No</label>
                  <input
                    type="text"
                    name="officeNo"
                    value={editForm.officeNo}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password (optional)</label>
                  <input
                    type="password"
                    name="password"
                    value={editForm.password}
                    onChange={handleEditChange}
                    placeholder="Leave blank to keep existing password"
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Number (optional)</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={editForm.vehicleNumber}
                    onChange={handleEditChange}
                    placeholder="Primary vehicle number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Vehicle Numbers</label>
                  {editForm.vehicleNumbers.map((vn, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={vn}
                        onChange={(e) => {
                          const newNumbers = [...editForm.vehicleNumbers];
                          newNumbers[index] = e.target.value;
                          setEditForm({ ...editForm, vehicleNumbers: newNumbers });
                        }}
                        placeholder="Additional vehicle number"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newNumbers = editForm.vehicleNumbers.filter((_, i) => i !== index);
                          setEditForm({ ...editForm, vehicleNumbers: newNumbers });
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, vehicleNumbers: [...editForm.vehicleNumbers, ''] })}
                    className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    + Add Vehicle Number
                  </button>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Phone Numbers</label>
                  {editForm.phoneNumbers.map((pn, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={pn}
                        onChange={(e) => {
                          const newNumbers = [...editForm.phoneNumbers];
                          newNumbers[index] = e.target.value;
                          setEditForm({ ...editForm, phoneNumbers: newNumbers });
                        }}
                        placeholder="Additional phone number"
                        maxLength={10}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newNumbers = editForm.phoneNumbers.filter((_, i) => i !== index);
                          setEditForm({ ...editForm, phoneNumbers: newNumbers });
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, phoneNumbers: [...editForm.phoneNumbers, ''] })}
                    className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    + Add Phone Number
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Office Modal */}
      {showAddOfficeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddOfficeModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Add Another Office</h3>
              <p className="text-sm text-gray-500 mt-1">Adding a new office for <strong>{addOfficeForm.name}</strong> ({addOfficeForm.contactNo})</p>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const data = new FormData();
                data.append('name', addOfficeForm.name);
                data.append('contactNo', addOfficeForm.contactNo);
                data.append('email', addOfficeForm.email);
                data.append('shopName', addOfficeForm.shopName);
                data.append('squareFeet', addOfficeForm.squareFeet);
                data.append('officeNo', addOfficeForm.officeNo);

                await shopkeeperService.create(data);
                setShowAddOfficeModal(false);
                loadShopkeepers();
              } catch (error: any) {
                alert(error.response?.data?.message || 'Error adding office');
              }
            }} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name *</label>
                  <input
                    type="text"
                    value={addOfficeForm.shopName}
                    onChange={(e) => setAddOfficeForm({ ...addOfficeForm, shopName: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g. My Second Shop"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Office No *</label>
                  <input
                    type="text"
                    value={addOfficeForm.officeNo}
                    onChange={(e) => setAddOfficeForm({ ...addOfficeForm, officeNo: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g. B-102"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Feet *</label>
                  <input
                    type="number"
                    value={addOfficeForm.squareFeet}
                    onChange={(e) => setAddOfficeForm({ ...addOfficeForm, squareFeet: e.target.value })}
                    min="0.1"
                    step="any"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddOfficeModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                >
                  Add Office
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shopkeepers;
